import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';

import { GroupType } from '@app/core/enums';
import { KeyValuePair } from '@app/models/key-value.model';
import { IdNameWidgetResponse } from '@app/models/response.model';
import { UserChat, ConversationType } from '@app/modules/message-center/models/chat.model';
import { MessageType } from '@app/modules/message-center/shared/constants';
import { formatRelativeDay } from '@app/common/utils';

@Component({
  selector: 'app-chats-list-item',
  templateUrl: './chats-list-item.component.html',
  styleUrl: './chats-list-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChatsListItemComponent {
  chat = input<UserChat>();
  userId = input<number>(null);
  isActive = input<boolean>(false);

  avatars = computed<IdNameWidgetResponse[]>(() => {
    const hasImage = !!this.chat()?.image;
    const isGroup = this.chat()?.conversation_type === ConversationType.group;
    const isPrivate = this.chat()?.access_type === GroupType.Private;

    if (hasImage || !isGroup || !isPrivate) {
      return null;
    }

    return (this.chat()?.members ?? []).map(m => ({ id: m.avatar, name: m.short_name }));
  });
  isMine = computed<boolean>(() => this.chat()?.last_message.sender?.id === this.userId());
  lastMessageTime = computed<string>(() => {
    const createdAt = this.chat()?.last_message?.created_at;

    if (!createdAt) {
      return null;
    }

    const dayJSdateTime = dayjs(createdAt);

    return formatRelativeDay(
      createdAt,
      'MMM D, h:mm A',
      dayJSdateTime.format('hh:mm A'),
      this.translate.instant('common.yesterday')
    );
  });

  chatSelected = output<UserChat>();
  deleteChat = output<void>();

  readonly ConversationType = ConversationType;
  readonly MessageType = MessageType;
  readonly lastMessageType: KeyValuePair<{ icon: string; class: string }, MessageType> = {
    [MessageType.internal]: {
      icon: 'chat',
      class: 'internal'
    },
    [MessageType.email]: {
      icon: 'mail',
      class: 'email'
    },
    [MessageType.sms]: {
      icon: 'sms',
      class: 'sms'
    }
  };

  constructor(
    private translate: TranslateService
  ) {}
}
